/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Divider, Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import PageNavigation from 'bundles/unified-description-page-common/components/modules/product-overview/PageNavigation';
import type { NavigationLink } from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import TranslationsNotificationBanner from 'bundles/unified-description-page-common/components/shared/TranslationsNotificationBanner';
import { PAGE_CONTENT_CONTAINER_NAME } from 'bundles/unified-description-page-common/constants';
import {
  NARROW_XDP_CONTAINER_QUERY,
  twoPanelGridItemFullWidth,
} from 'bundles/unified-description-page-common/constants/styles';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useTranslationsNotificationBanner } from 'bundles/unified-description-page-common/contexts/consumer/TranslationsNotificationBannerContext';
import {
  XDP_KEY_INFORMATION_SLOT_CLASS_NAME,
  XDP_PAGE_GRID_CONTAINER_CLASS_NAME,
} from 'bundles/unified-description-page-common/pages/embedded/xdpClassNames';

type Props = {
  heroBackgroundColor?: string;
  hero: React.ReactNode;
  navigationLinks?: Array<NavigationLink>;
  anchorId?: string;
  secondaryInfo?: React.ReactNode;
  keyInformation?: React.ReactNode;
  aboutSubmodule?: React.ReactNode;
  learnerStatsModule?: React.ReactNode;
  disablePageNavigationDisplay?: boolean;
  overviewBackgroundImage?: React.ReactNode;
  isCompact?: boolean;
};

const styles = {
  sectionContainer: css`
    position: relative;

    /* fallback to hidden if clip is not supported */
    overflow-x: hidden;
    overflow-x: clip;
  `,
  heroContainer: css`
    position: relative;
  `,
  heroKIMOffset: css`
    /* stylelint-disable-next-line no-empty-source */
  `,
  heroInnerWrapper: css`
    padding-left: var(--cds-spacing-400);
  `,
  heroBackground: (heroBackgroundColor?: string, translationBannerIsVisible?: boolean) => css`
    background-color: ${heroBackgroundColor ?? 'transparent'};
    position: absolute;
    width: 200vw;
    left: -50vw;
    top: ${translationBannerIsVisible ? -150 : 0}px;
    bottom: 0;
    z-index: -1;
    ${
      translationBannerIsVisible &&
      `
    ${breakpoints.down('xs')} {
      top: -200px;
    }
`
    }
  `,
  mobileInfoSection: css`
    margin-top: var(--cds-spacing-100);
    width: 100%;
  `,
  desktopInfoSection: css`
    width: 100%;
  `,
  mobileHorizontalDivider: css`
    width: 100%;
    left: 0;
    position: absolute;
    margin: var(--cds-spacing-300) 0;
    height: 2px;
  `,
  aboutSubModule: css`
    && {
      ${breakpoints.up('md')} {
        padding-top: 0;
      }
    }
  `,
  learnerStatsModule: css`
    display: flex;
    flex-direction: column;
    align-items: end;

    ${NARROW_XDP_CONTAINER_QUERY} {
      align-items: start;
    }
  `,
  pageNavLinks: css`
    && {
      padding-bottom: 0;
    }

    /* In chat-docked narrow mode the chat replaces page navigation. Hide the
    /* visible nav but keep the Grid wrapper in flow (visibility:hidden + height:0
    /* instead of display:none) so PageNavigation's scroll listener still drives
    /* showFixedPageNav correctly. */
    ${NARROW_XDP_CONTAINER_QUERY} {
      && {
        visibility: hidden;
        height: 0;
        overflow: hidden;
        padding: 0;
        margin: 0;
      }
    }
  `,
  /*
  We have to offset the PageNavigation padding because we cant avoid rendering the empty component.
  It's required for some complex calculations in a Provider that will be reworked when this functionality
  is rolled out.
  */
  pageNavLinksRedesign: css`
    margin-top: calc(var(--cds-spacing-200) * -1);
  `,
};

const isCompactStyles = {
  sectionContainer: css`
    padding-top: 0;
  `,
  heroBackground: (heroBackgroundColor?: string) => css`
    background-color: ${heroBackgroundColor ?? 'transparent'};
    border-radius: var(--cds-border-radius-200);
    padding: var(--cds-spacing-200);
  `,
};

// Two-panel-mode overrides scoped via @container — apply only when nested inside
// TwoPanelConversationalDiscoveryLayout's contentColumn (md+). Outside two-panel mode the
// rules are dormant.
const twoPanelStyles = {
  // Drop overflow-x: clip so the hero bg can extend through the contentColumn's gutter
  // padding to the viewport edge.
  sectionContainer: css`
    @container ${PAGE_CONTENT_CONTAINER_NAME} (min-width: 0) {
      overflow-x: visible;
    }
  `,
};

const ProductOverviewHorizontalModule = ({
  heroBackgroundColor,
  hero,
  navigationLinks,
  anchorId,
  secondaryInfo,
  keyInformation,
  aboutSubmodule,
  learnerStatsModule,
  disablePageNavigationDisplay,
  overviewBackgroundImage,
  isCompact,
}: Props) => {
  const userAgent = useUserAgent();
  const eventingProductData = useEventingProductData();
  const { showBanner } = useTranslationsNotificationBanner();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'about',
      },
      ...eventingProductData,
    }
  );

  const renderAboutSubmodule = () => {
    if (!aboutSubmodule) {
      return null;
    }

    return (
      <>
        <Grid item xs={12} md={8} css={[styles.aboutSubModule, twoPanelGridItemFullWidth]}>
          <Section anchorId={anchorId} spacing={0} ref={visibilityTrackingRef}>
            {aboutSubmodule}
          </Section>
        </Grid>
        {learnerStatsModule && (
          <Grid item xs={12} md={4} css={[styles.learnerStatsModule, twoPanelGridItemFullWidth]}>
            {learnerStatsModule}
          </Grid>
        )}
      </>
    );
  };

  return (
    <section
      css={[styles.sectionContainer, twoPanelStyles.sectionContainer, isCompact && isCompactStyles.sectionContainer]}
    >
      {overviewBackgroundImage && <React.Fragment>{overviewBackgroundImage}</React.Fragment>}
      <PageGridContainer className={XDP_PAGE_GRID_CONTAINER_CLASS_NAME}>
        <Grid container item css={styles.heroContainer}>
          {showBanner && (
            <Grid item xs={12}>
              <TranslationsNotificationBanner />
            </Grid>
          )}
          <Grid item xs={12}>
            <div css={[styles.heroKIMOffset, isCompact && isCompactStyles.heroBackground(heroBackgroundColor)]}>
              {hero}
            </div>
          </Grid>
          {/*
           * The background color of the hero banner needs to flush all the way to the edge of the browser window.
           * The div below is absolute-positioned with a height of its parent container and a width of 200vw.
           * To prevent the horizontal scrollbar appears because of the 200vw width,
           * overflow-x: clip is added to the top most element of the component.
           */}
          {!isCompact && <div css={styles.heroBackground(heroBackgroundColor, showBanner)} />}
        </Grid>
        <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) =>
            matches ? (
              <Grid item css={styles.mobileInfoSection} data-e2e="key-information">
                <PageNavigation
                  navigationLinks={navigationLinks}
                  disablePageNavigationDisplay={disablePageNavigationDisplay}
                />
                {secondaryInfo && (
                  <>
                    {secondaryInfo}
                    <Divider css={styles.mobileHorizontalDivider} />
                  </>
                )}
                <div className={XDP_KEY_INFORMATION_SLOT_CLASS_NAME}>{keyInformation}</div>
              </Grid>
            ) : (
              <>
                <Grid item css={styles.desktopInfoSection} data-e2e="key-information">
                  <div className={XDP_KEY_INFORMATION_SLOT_CLASS_NAME}>{keyInformation}</div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={8}
                  css={[
                    styles.pageNavLinks,
                    disablePageNavigationDisplay ? styles.pageNavLinksRedesign : undefined,
                    twoPanelGridItemFullWidth,
                  ]}
                >
                  <PageNavigation
                    navigationLinks={navigationLinks}
                    disablePageNavigationDisplay={disablePageNavigationDisplay}
                  />
                </Grid>
              </>
            )
          }
        </Media>
        {renderAboutSubmodule()}
      </PageGridContainer>
    </section>
  );
};

export default ProductOverviewHorizontalModule;
